/**
 * CLEANING SERVICE — Mobile-ready business logic layer
 */
import hosys from "@/api/api";
import moment from "moment";

export const ZONE_LABELS = {
  kitchen:   "Cuina",
  bar:       "Barra",
  dining:    "Menjador",
  bathrooms: "Banys",
  storage:   "Magatzem",
  chambers:  "Cambres",
  machinery: "Maquinària",
  terrace:   "Terrassa",
};

export const DEFAULT_TASKS_BY_ZONE = {
  kitchen:   ["Netejar superfícies de treball", "Desinfectar taulers de tall", "Netejar fogons i forn", "Buidar escombraries", "Fregar el terra"],
  bar:       ["Netejar barra i taulell", "Netejar màquina de cafè", "Desinfectar dosificadors", "Netejar nevera de begudes"],
  dining:    ["Netejar taules i cadires", "Passar l'aspiradora", "Fregar el terra", "Netejar finestrals"],
  bathrooms: ["Netejar i desinfectar vàters", "Netejar lavabos", "Reposar paper i sabó", "Fregar el terra"],
  storage:   ["Revisar ordre i neteja", "Netejar prestatgeries"],
  machinery: ["Netejar fregidores", "Netejar vitrina expositora", "Revisar i netejar campana extractora"],
};

export async function createCleaningTask(venueId, user, data) {
  const frequency = data.frequency || "daily";
  const today = moment();
  const datesToCreate = [today];

  // Generate dates for the next 30 days based on frequency
  if (frequency !== "daily") {
    let current = today.clone();
    while (current.isBefore(today.clone().add(30, "days"))) {
      if (shouldCreateTaskForDate(current, frequency)) {
        datesToCreate.push(current.clone());
      }
      current.add(1, "day");
    }
  } else {
    // For daily tasks, create for next 30 days
    for (let i = 1; i <= 30; i++) {
      datesToCreate.push(today.clone().add(i, "days"));
    }
  }

  // Create tasks with limited concurrency (max 3 at a time)
  return batchCreateRecords(
    datesToCreate.map(date => ({
      venue_id: venueId,
      zone: data.zone,
      task: data.task,
      completed: false,
      responsible_email: user.email,
      responsible_name: user.full_name,
      frequency: frequency,
      observations: data.observations || "",
      date: date.format("YYYY-MM-DD"),
    }))
  );
}

async function batchCreateRecords(records, batchSize = 3) {
  const results = [];
  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(r => hosys.entities.CleaningRecord.create(r))
    );
    results.push(...batchResults);
    // Small delay between batches to avoid rate limit
    if (i + batchSize < records.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  return results;
}

function shouldCreateTaskForDate(date, frequency) {
  const today = moment();
  const diffDays = date.diff(today, "days");

  switch (frequency) {
    case "hourly":
      return diffDays <= 1; // Only today and tomorrow
    case "weekly":
      return date.day() === today.day(); // Same day of week
    case "biweekly":
      return (diffDays % 14) === 0; // Every 14 days
    case "monthly":
      return date.date() === today.date(); // Same day of month
    case "quarterly":
      return date.quarter() === today.quarter() && date.date() === today.date();
    case "yearly":
      return date.dayOfYear() === today.dayOfYear();
    default:
      return false;
  }
}

export async function toggleCleaningTask(recordId, completed) {
  return hosys.entities.CleaningRecord.update(recordId, { completed });
}

export async function getCleaningRecords(venueId, date) {
  return hosys.entities.CleaningRecord.filter({ venue_id: venueId, date }, "zone");
}

export async function getDailyCleaningSummary(venueId, date) {
  const records = await getCleaningRecords(venueId, date);
  const completed = records.filter(r => r.completed).length;
  return {
    total: records.length,
    completed,
    pending: records.length - completed,
    pct: records.length > 0 ? Math.round((completed / records.length) * 100) : 0,
    records,
    allDone: records.length > 0 && completed === records.length,
  };
}

export async function addPresetTasksForZone(venueId, user, zone) {
  const tasks = DEFAULT_TASKS_BY_ZONE[zone] || [];
  return Promise.all(tasks.map(task => createCleaningTask(venueId, user, { zone, task })));
}

export async function repeatRecurringTasksNextMonth(venueId, user) {
  const today = moment();
  const currentMonth = today.format("YYYY-MM");
  const nextMonth = today.clone().add(1, "month").format("YYYY-MM");

  // Get all recurring tasks from current month
  const startOfMonth = moment(currentMonth).startOf("month");
  const endOfMonth = moment(currentMonth).endOf("month");

  const currentMonthTasks = await hosys.entities.CleaningRecord.filter({
    venue_id: venueId,
    date: { $gte: startOfMonth.format("YYYY-MM-DD"), $lte: endOfMonth.format("YYYY-MM-DD") },
  });

  // Filter only recurring tasks (frequency != "daily" or with specific patterns)
  const recurringTasks = currentMonthTasks.filter(t => 
    t.frequency && ["weekly", "biweekly", "monthly", "quarterly", "yearly"].includes(t.frequency)
  );

  // Group by task + zone + frequency to avoid duplicates
  const uniqueTasks = [];
  const seen = new Set();

  for (const task of recurringTasks) {
    const key = `${task.zone}-${task.task}-${task.frequency}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueTasks.push(task);
    }
  }

  // Create same tasks for next month on appropriate dates
  const nextMonthStart = moment(nextMonth).startOf("month");
  const nextMonthEnd = moment(nextMonth).endOf("month");

  return Promise.all(
    uniqueTasks.map(async (task) => {
      const datesToCreate = [];
      let current = nextMonthStart.clone();

      while (current.isBefore(nextMonthEnd) || current.isSame(nextMonthEnd)) {
        if (shouldCreateTaskForDate(current, task.frequency)) {
          datesToCreate.push(current.clone());
        }
        current.add(1, "day");
      }

      return Promise.all(
        datesToCreate.map(date =>
          hosys.entities.CleaningRecord.create({
            venue_id: venueId,
            zone: task.zone,
            task: task.task,
            completed: false,
            responsible_email: task.responsible_email,
            responsible_name: task.responsible_name,
            frequency: task.frequency,
            observations: task.observations || "",
            date: date.format("YYYY-MM-DD"),
          })
        )
      );
    })
  );
}